/**
 * ⭐ Indikator kesehatan di layar kasir — penerapan **R2 (§10b): kegagalan WAJIB TERLIHAT.**
 *
 * ### Kenapa ini bukan hiasan
 * Tiket dapur yang hilang karena timeout adalah kegagalan senyap paling mahal di jam sibuk: kasir
 * mengira pesanan terkirim, dapur tak pernah menerimanya, dan **yang pertama tahu adalah tamu yang
 * menunggu**. Riset kita menemukan pola yang sama di tempat lain — `RewardPointHistory` Arena
 * menyimpan 4.547 baris pending + 719 error **bertahun-tahun** tanpa ada yang tahu.
 *
 * ### ⛔ Aturan keras: JANGAN PERNAH menampilkan hijau palsu
 * Yang belum tersambung ditampilkan **`unknown` (abu-abu)**, bukan hijau. Indikator yang berbohong
 * lebih buruk daripada tak ada indikator — ia membuat orang berhenti memeriksa. Karena itu status
 * printer saat ini `unknown`: `device-agent` (K6) belum dibangun, dan berpura-pura tahu adalah
 * persis kesalahan yang komponen ini ada untuk mencegahnya.
 */
import { useTranslation } from 'react-i18next';


export type HealthState = 'healthy' | 'slow' | 'down' | 'checking' | 'unknown';

export interface HealthStatus {
  readonly server: HealthState;
  /** `unknown` sampai `device-agent` ada. Lihat aturan keras di atas. */
  readonly printer: HealthState;
  /** ⚠️ Sebab persisnya — `slow` bisa berarti "belum di-pair" ATAU "origin ditolak". */
  readonly printerDetail?: 'down' | 'rejected' | 'no-printer' | 'ready';
  /** Jumlah transaksi menunggu kirim; `undefined` = belum ada outbox (Fase 3). */
  readonly queued?: number;
  /**
   * ⭐⭐ Yang tertahan karena DITOLAK portal — menunggu **manusia**, bukan jaringan.
   *
   * ⛔ Dipisah dari `queued` dengan sengaja: angka yang tak pernah turun, kalau dicampur, terlihat
   * seperti jaringan yang sedang jelek — dan orang menunggu sesuatu yang tak akan pernah beres
   * sendiri.
   */
  readonly needsReview?: number;
  readonly lastOk?: string;
}

const COLORS: Readonly<Record<HealthState, string>> = {
  healthy: '#1a7f37',
  slow: '#bf8700',
  down: '#cf222e',
  checking: '#57606a',
  unknown: '#8c959f',
};

/**
 * @param href bila diisi, SELURUH lencana jadi tautan.
 *
 * ⭐ Imron, 2026-08-22: *"di ketuk tidak keluar tautan"* — dan itu instingnya yang benar. Lencana
 * yang mengabarkan masalah adalah benda pertama yang diketuk orang; menaruh jalan keluarnya di
 * kalimat sebelahnya berarti mengandalkan orang membaca sebelum menyentuh. Mereka tidak.
 */
function Dot(
  { status, label, href }: { status: HealthState; label: string; href?: string },
) {
  const { t } = useTranslation();
  const text =
    status === 'unknown' ? '—' : t(`health.status.${status}`);
  const isi = (
    <>
      <span className="health-dot" style={{ background: COLORS[status] }} aria-hidden="true" />
      <span className="health-label">{label}</span>
      <span className="health-value">{text}</span>
    </>
  );
  if (href === undefined) {
    return (
      <span className="health-item" data-testid={`health-${label}`} data-status={status}>
        {isi}
      </span>
    );
  }
  return (
    <a
      className="health-item health-item-link"
      data-testid={`health-${label}`}
      data-status={status}
      href={href}
    >
      {isi}
    </a>
  );
}

export function HealthIndicator(
  { status, queues }: { status: HealthStatus; queues?: boolean },
) {
  const { t } = useTranslation();
  const offline = status.server === 'down';

  return (
    <div className="health" role="status" aria-live="polite" data-testid="health">
      <Dot status={status.server} label={t('health.server')} />
      {/* ⛔ Tautan hanya saat agennya memang tak ada DAN perangkatnya Android — lencana yang
          mengantar ke APK di mesin desktop menyesatkan. */}
      <Dot
        status={status.printer}
        label={t('health.kitchenPrinter')}
        {...(status.printer === 'down' ? { href: '/kiosk/agent.apk' } : {})}
      />
      {status.queued !== undefined && status.queued > 0 && (
        <span className="health-item" data-testid="health-queued">
          <span className="health-label">{t('health.queue')}</span>
          <span className="health-value">
            {t('health.queueHeld', { n: status.queued })}
          </span>
        </span>
      )}
      {status.needsReview !== undefined && status.needsReview > 0 && (
        <span className="health-item health-review" data-testid="health-review">
          <span className="health-label">{t('health.review')}</span>
          <span className="health-value">
            {t('health.reviewHeld', { n: status.needsReview })}
          </span>
        </span>
      )}
      {/*
        ⚠️ Pesan luring menegaskan transaksi TETAP tersimpan. Tanpa kalimat itu, kasir cenderung
        mengulang input — dan pengulangan itulah yang menjadi transaksi ganda saat jaringan pulih.

        ⛔⛔ **Tapi janji itu hanya benar di tier yang PUNYA antrean** (LU-BATAS-02, UAT
        2026-08-29). Terukur di till tier `server`: spanduk berbunyi *"transaksi tetap tersimpan
        dan akan dikirim otomatis"* sementara `localStorage` tak punya kunci `osbpos.outbox` sama
        sekali — yang ada cuma salinan katalog & verifier PIN. Bill-nya bahkan tak bisa dihitung.
        ⇒ kasir diberi tahu penjualannya aman padahal ia tak ada di mana pun.

        ⚠️ Ini persis pola "setelan yang berbohong" yang sudah berkali-kali dikoreksi di proyek
        ini, dan di sini akibatnya paling mahal: yang dipercaya kasir adalah UANG.
      */}
      {offline && (
        <span className="health-message" data-testid="health-offline">
          {queues === false ? t('health.offlineNoQueue') : t('health.offline')}
        </span>
      )}
      {status.printer === 'down' && (
        <span className="health-message" data-testid="health-printer-off">
          {t('health.printerOff')}
          {/*
            ⛔⛔ **TANPA deteksi platform** (2026-08-22). Versi sebelumnya menyaring `isAndroid()`
            dari `userAgent` — dan itu GAGAL persis di perangkat sasarannya: Chrome di **tablet**
            Android meminta "situs desktop" secara bawaan, jadi UA-nya tak memuat "Android" sama
            sekali. Tombolnya tak pernah muncul di satu-satunya tempat ia dibutuhkan.

            ⚠️ Salah di arah sebaliknya jauh lebih murah: pengguna desktop melihat satu tombol yang
            tak berguna baginya. Pengguna tablet melihat NOL jalan keluar.

            ⚠️ Jalur RELATIF — satu bundel dipakai semua host (K21).
          */}
          <a className="health-install" data-testid="health-agent-apk" href="/kiosk/agent.apk">
            {t('health.installAgent')}
          </a>
        </span>
      )}
      {/* ⚠️ Pesan TERSENDIRI, bukan versi lembut dari yang di atas: yang harus dikerjakan berbeda —
          yang satu memasang aplikasi, yang satu mem-pair printer. Pesan yang sama untuk dua
          pekerjaan berbeda membuat orang mengerjakan yang salah lebih dulu. */}
      {status.printer === 'slow' && (
        <span
          className="health-message"
          data-testid={status.printerDetail === 'rejected'
            ? 'health-printer-rejected' : 'health-printer-unpaired'}
        >
          {t(status.printerDetail === 'rejected'
            ? 'health.printerRejected' : 'health.printerUnpaired')}
          {status.printerDetail === 'rejected' && ` (${location.origin})`}
        </span>
      )}
    </div>
  );
}
